import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { authFetch } from './authFetch';
import { useTheme } from '../contexts/ThemeContext';
import '../css/App.css';
import flagKr from '../images/flag-kr.svg';
import flagEn from '../images/flag-en.svg';
import flagCh from '../images/flag-ch.svg';
import flagJp from '../images/flag-jp.svg';

const Sidebar = ({ isMenuVisible, setIsMenuVisible }) => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { isAuthenticated, isAdminUser } = useAuth();
    const { themeMode, toggleTheme } = useTheme();

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
        localStorage.setItem('language', lng);
        if (isAuthenticated) {
            authFetch('/api/v1/user/language', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ language: lng }),
            }).catch(() => {});
        }
    };

    const getThemeIcon = () => {
        switch (themeMode) {
            case 'light': return '\u2600'; // ☀
            case 'dark': return '\u25CF';  // ●
            case 'system': return '\u25D0'; // ◐
            default: return '\u2600';
        }
    };

    const getThemeLabel = () => {
        switch (themeMode) {
            case 'light': return t('theme.light');
            case 'dark': return t('theme.dark');
            case 'system': return t('theme.system');
            default: return t('theme.light');
        }
    };

    const handleNavigate = (path) => {
        navigate(path);
        // 모바일에서 메뉴 아이템 클릭 시 드로어 자동 닫기
        if (window.innerWidth <= 768) {
            setIsMenuVisible(false);
        }
    };

    return (
        <>
            <div className="menu-toggle-container">
                <button
                    className="sidebar-toggle-btn"
                    onClick={() => {
                        setIsMenuVisible(!isMenuVisible);
                    }}
                >
                    {isMenuVisible
                        ? <span className="sidebar-toggle-icon sidebar-toggle-close">{'\u2715'}</span>
                        : <span className="sidebar-toggle-icon sidebar-toggle-open">{'\u2630'}</span>
                    }
                </button>
            </div>

            {/* 모바일 오버레이 백드롭 */}
            {isMenuVisible && (
                <div
                    className="sidebar-overlay"
                    onClick={() => setIsMenuVisible(false)}
                />
            )}

            <nav className={`nav-menu${isMenuVisible ? ' nav-menu-open' : ''}`}>
                <div className="menu-content">
                    <ul>
                        <li onClick={() => handleNavigate('/announcements')}><span className="nav-icon">&#9993;</span>{t('nav.announcements')}</li>
                        <li onClick={() => handleNavigate('/')}><span className="nav-icon">&#8962;</span>{t('nav.home')}</li>
                        <li onClick={() => handleNavigate('/about')}><span className="nav-icon">&#9432;</span>{t('nav.about')}</li>
                        {!isAuthenticated && (
                            <li onClick={() => handleNavigate('/login')}><span className="nav-icon">&#10145;</span>{t('nav.login')}</li>
                        )}
                        {isAuthenticated && (
                            <>
                                <li onClick={() => handleNavigate('/timetable')}><span className="nav-icon">&#9638;</span>{t('nav.timetable')}</li>
                                <li onClick={() => handleNavigate('/recurring')}><span className="nav-icon">&#8634;</span>{t('nav.recurring')}</li>
                                <li onClick={() => handleNavigate('/subjects')}><span className="nav-icon">&#9776;</span>{t('nav.subjects')}</li>
                                <li onClick={() => handleNavigate('/daily')}><span className="nav-icon">&#9745;</span>{t('nav.daily')}</li>
                                <li onClick={() => handleNavigate('/timer')}><span className="nav-icon">&#9201;</span>{t('nav.timer')}</li>
                                <li onClick={() => handleNavigate('/vocabulary')}><span className="nav-icon">&#9707;</span>{t('nav.vocabulary')}</li>
                                <li onClick={() => handleNavigate('/statistics')}><span className="nav-icon">&#8862;</span>{t('nav.statistics')}</li>
                                <li onClick={() => handleNavigate('/observer')}><span className="nav-icon">&#9673;</span>{t('nav.observer')}</li>
                                <li onClick={() => handleNavigate('/studyroom')}><span className="nav-icon">&#8852;</span>{t('nav.studyroom')}</li>
                                <li onClick={() => handleNavigate('/board')}><span className="nav-icon">&#9998;</span>{t('nav.board')}</li>
                                <li onClick={() => handleNavigate('/personalinfo')}><span className="nav-icon">&#9881;</span>{t('nav.personalinfo')}</li>
                            </>
                        )}
                        {isAdminUser && (
                            <li onClick={() => handleNavigate('/admin')}><span className="nav-icon">&#9733;</span>{t('nav.admin')}</li>
                        )}

                        {isAuthenticated && (
                            <li onClick={() => handleNavigate('/logout')}><span className="nav-icon">&#10550;</span>{t('nav.logout')}</li>
                        )}
                    </ul>
                </div>

                <div className="sidebar-bottom-items">
                    <button
                        className="theme-toggle"
                        onClick={(e) => { e.stopPropagation(); toggleTheme(); }}
                        title={getThemeLabel()}
                    >
                        <span>{getThemeIcon()}</span>
                        <span className="theme-toggle-label">{getThemeLabel()}</span>
                    </button>

                    <span className="sidebar-privacy-link" onClick={() => handleNavigate('/ai-usage')}>
                        {t('nav.aiUsage')}
                    </span>
                    <span className="sidebar-privacy-link" onClick={() => handleNavigate('/privacy')}>
                        {t('nav.privacy')}
                    </span>

                    <div className="language-selector">
                        {[
                            { code: 'kr', src: flagKr, alt: '한국어' },
                            { code: 'en', src: flagEn, alt: 'English' },
                            { code: 'ch', src: flagCh, alt: '中文' },
                            { code: 'jp', src: flagJp, alt: '日本語' },
                        ].map(({ code, src, alt }) => (
                            <img
                                key={code}
                                src={src}
                                alt={alt}
                                onClick={() => changeLanguage(code)}
                                className={`flag-btn${i18n.language === code ? ' active' : ''}`}
                                title={alt}
                            />
                        ))}
                    </div>
                </div>
            </nav>
        </>
    );
};

export default Sidebar;
